/**
 * @fileoverview チャットページ
 * @description P1デザイン対応のチャット画面（PC/モバイル対応）
 */
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, History, Bird, Info } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { useChat } from '@/hooks/use-chat'
import { MessageBubble, LoadingBubble } from '@/components/chat/message-bubble'
import { WelcomeSection } from '@/components/chat/template-card'
import { ChatInput } from '@/components/chat/chat-input'
import { MessageListSkeleton } from '@/components/chat/loading-skeleton'
import { ErrorMessage, RateLimitError } from '@/components/chat/error-message'
import { PdfViewerProvider, usePdfViewer } from '@/components/pdf/pdf-viewer-context'
import { PdfViewerDialog } from '@/components/pdf/pdf-viewer-dialog'

/**
 * 検索パラメータの型
 */
interface ChatSearchParams {
  template: string | undefined
}

/**
 * チャットページの内部コンポーネント
 * @description PDFビューアのContext内で使用
 */
function ChatPageContent() {
  const { template } = Route.useSearch()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const {
    messages,
    templates,
    isLoadingTemplates,
    isSending,
    error,
    remainingMessages,
    sendMessage,
    clearError,
    useTemplate,
  } = useChat()

  // テンプレートIDがURLにある場合、初回レンダリング時に使用
  useEffect(() => {
    if (template && templates.length > 0) {
      const foundTemplate = templates.find((t) => t.id === template)
      if (foundTemplate) {
        useTemplate(foundTemplate)
        // URLからテンプレートパラメータを削除
        window.history.replaceState({}, '', '/chat')
      }
    }
  }, [template, templates, useTemplate])

  // メッセージが追加されたら自動スクロール（初期表示時はスクロールしない）
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // レート制限エラーの判定
  const isRateLimited = error?.includes('制限')

  return (
    <div className="flex min-h-screen flex-col bg-[#F5F4F1]">
      {/* PC版レイアウト */}
      <div className="hidden h-screen lg:flex">
        {/* 左パネル（ホームと同様） */}
        <div className="flex flex-1 flex-col gap-10 overflow-auto p-12 xl:p-16">
          {/* ヘッダー */}
          <header className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F5E9]">
                <Bird className="h-5 w-5 text-[#3D8A5A]" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-lg font-bold text-[#1A1918]">家老空兵衛</span>
                <span className="text-xs text-[#6D6C6A]">ドローン法規ナビ</span>
              </div>
            </Link>
          </header>

          {/* ヒーローセクション（P1デザイン準拠） */}
          <section className="flex flex-col gap-6">
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-[#1A1918] xl:text-5xl">
              ドローン飛行の法規を
              <br />
              大空藩がご案内
            </h1>
            <p className="text-base leading-relaxed text-[#6D6C6A] xl:text-lg">
              家老・空兵衛と若侍・誠之介が、免許、機体登録、飛行エリアの制限など、
              <br />
              ドローン飛行に関する法規や手続きについてお答えします。
            </p>
          </section>

          {/* 残り回数表示（未ログイン時） */}
          {remainingMessages !== undefined && remainingMessages < 10 && (
            <div className="flex items-center gap-2.5 rounded-xl border border-[#FDBA74] bg-[#FFF7ED] px-4 py-3">
              <Info className="h-4 w-4 text-[#F97316]" />
              <span className="text-sm text-[#9A3412]">
                残り{remainingMessages}回質問できます（1日10回まで）
              </span>
            </div>
          )}
        </div>

        {/* 右パネル（チャット） */}
        <div className="flex w-[480px] flex-col border-l border-[#E5E4E1] bg-white">
          {/* チャットヘッダー */}
          <div className="flex items-center gap-3 border-b border-[#E5E4E1] px-6 py-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C8E6C9]">
              <Bird className="h-4 w-4 text-[#2E7D32]" />
            </div>
            <div className="flex flex-1 flex-col gap-0.5">
              <span className="text-base font-semibold text-[#1A1918]">大空藩 飛行御用</span>
              <span className="text-[13px] text-[#3D8A5A]">オンライン</span>
            </div>
            <button type="button" className="text-[#6D6C6A] hover:text-[#1A1918]" title="履歴">
              <History className="h-6 w-6" />
            </button>
          </div>

          {/* メッセージエリア */}
          <div className="flex flex-1 flex-col gap-5 overflow-auto p-6">
            {/* エラー表示 */}
            {error &&
              (isRateLimited ? (
                <RateLimitError onDismiss={clearError} />
              ) : (
                <ErrorMessage message={error} onDismiss={clearError} />
              ))}

            {/* ウェルカムセクション or メッセージ一覧 */}
            {messages.length === 0 ? (
              isLoadingTemplates ? (
                <MessageListSkeleton count={3} />
              ) : (
                <WelcomeSection templates={templates} onSelectTemplate={useTemplate} />
              )
            ) : (
              <>
                {messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
                {isSending && <LoadingBubble />}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* 入力エリア */}
          <div className="border-t border-[#E5E4E1] bg-white p-4">
            <ChatInput onSend={sendMessage} isLoading={isSending} placeholder="質問を入力..." />
          </div>
        </div>
      </div>

      {/* モバイル版レイアウト（入力欄をposition: fixedで固定し、dvhの計算遅延問題を回避） */}
      <div className="flex min-h-screen flex-col lg:hidden">
        {/* ヘッダー（sticky: スクロール時も上部に固定） */}
        <header className="sticky top-0 z-10 flex items-center gap-4 border-b border-[#E5E4E1] bg-white px-6 py-3">
          <Link to="/" className="text-[#1A1918]">
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="text-[17px] font-semibold text-[#1A1918]">大空藩 飛行御用</span>
            <span className="text-xs font-medium text-[#3D8A5A]">家老・若侍がお仕えします</span>
          </div>
          <button type="button" className="text-[#6D6C6A] hover:text-[#1A1918]" title="履歴">
            <History className="h-6 w-6" />
          </button>
        </header>

        {/* メッセージエリア（pb-24: 入力エリア分の余白を確保） */}
        <div className="flex flex-1 flex-col gap-5 overflow-auto bg-[#F5F4F1] p-6 pb-24">
          {/* エラー表示 */}
          {error &&
            (isRateLimited ? (
              <RateLimitError onDismiss={clearError} />
            ) : (
              <ErrorMessage message={error} onDismiss={clearError} />
            ))}

          {/* ウェルカムセクション or メッセージ一覧 */}
          {messages.length === 0 ? (
            isLoadingTemplates ? (
              <MessageListSkeleton count={3} />
            ) : (
              <WelcomeSection templates={templates} onSelectTemplate={useTemplate} />
            )
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              {isSending && <LoadingBubble />}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* モバイル版入力エリア（fixed: 画面下部に固定、pb-safe: Safe Area対応） */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[#E5E4E1] bg-white p-4 pb-safe lg:hidden">
        <ChatInput onSend={sendMessage} isLoading={isSending} placeholder="質問を入力..." />
      </div>
    </div>
  )
}

/**
 * PDFビューアダイアログを表示するラッパー
 * @description Context内でのみ使用可能
 */
function PdfViewerWrapper() {
  const { state, closePdfViewer } = usePdfViewer()

  return (
    <PdfViewerDialog
      isOpen={state.isOpen}
      onClose={closePdfViewer}
      pdfUrl={state.pdfUrl}
      initialPage={state.page}
    />
  )
}

/**
 * チャットページコンポーネント
 * @description PC版とモバイル版でレスポンシブに切り替え + PDFビューアダイアログ対応
 */
function ChatPage() {
  return (
    <PdfViewerProvider>
      <ChatPageContent />
      <PdfViewerWrapper />
    </PdfViewerProvider>
  )
}

/**
 * チャットルート定義
 */
export const Route = createFileRoute('/chat')({
  component: ChatPage,
  validateSearch: (search: Record<string, unknown>): ChatSearchParams => {
    return {
      template: typeof search.template === 'string' ? search.template : undefined,
    }
  },
})
